/**
 * @class
 * @memberOf P3
 */
export default class AssetManager {
    constructor() {
        /**
         * @type {Object}
         */
        this.signals = {};

        /**
         * @type {signals.Signal}
         */
        this.signals.progress = new signals.Signal();

        /**
         * @type {signals.Signal}
         */
        this.signals.complete = new signals.Signal();

        /**
         * @type {signals.Signal}
         */
        this.signals.error = new signals.Signal();

        /**
         * @type {PIXI.loaders.Loader}
         * @private
         */
        this._loader = new PIXI.loaders.Loader();
        this._loader.on("progress", this._onLoaderProgress, this);
        this._loader.on("complete", this._onLoaderComplete, this);
        this._loader.on("error", this._onLoaderError, this);

        /**
         * @type {Array}
         * @private
         */
        this._manifest = [];

        /**
         * @type {Object}
         * @private
         */
        this._resources = {};

        const graphics = new PIXI.Graphics();
        graphics.beginFill(0x0, 0.5);
        graphics.drawRect(0, 0, 100, 100);
        graphics.endFill();
        graphics.lineStyle(2, 0xFF0000, 1);
        graphics.moveTo(0, 0);
        graphics.lineTo(100, 100);
        graphics.moveTo(100, 0);
        graphics.lineTo(0, 100);

        try {
            this._fallbackTexture = graphics.generateTexture();
        } catch(e) {
            this._fallbackTexture = PIXI.Texture.EMPTY;
        }
    }

    /**
     * @param {Array} files
     * @param {string} [path]
     * @returns {Array}
     */
    addFiles(files, path = "") {
        if (path && path.length > 0) {
            for (let i = 0; i < files.length; ++ i) {
                const url = files[i].url;
                files[i].url = path + url;
            }
        }
        this._manifest = this._manifest.concat(files);
        return this._manifest;
    }

    /**
     */
    load() {
        if (!this._manifest.length) return;

        this._loader.add(this._manifest);
        this._loader.load();
        return this._manifest;
    }

    /**
     * @param {string} name
     * @param {string} [extension]
     */
    unloadTexture(name, extension) {
        extension = "." + (extension || "png");

        const resource = this._resources[name];
        const split = resource.url.split(".");
        const texture = PIXI.utils.TextureCache[split[0] + extension];
        texture.destroy(true);
    }

    /**
     * @param {string} name
     * @param {string} [extension]
     */
    texture(name, extension) {
        extension = "." + (extension || "png");

        let texture;
        try {
            texture = PIXI.Texture.fromFrame(name + extension);
        } catch (error) {
            if (this._resources[name]) {
                texture = this._resources[name].texture;
            } else {
                console.warn("texture not found : " + name);
                texture = this._fallbackTexture;
            }
        }
        return texture;
    }

    /**
     * @param {string} name
     * @returns {Object}
     */
    json(name) {
        const data = P3.utils.copyObject(this._resources[name].data);
        if (!data) {
            throw new Error("json not found: " + name);
        }
        return data;
    }

    /**
     * @param {string} name
     * @returns {Object}
     */
    spineData(name) {
        const resource = this._resources[name];
        if (!resource || (resource && !resource.spineData)) {
            throw new Error("spine data not found: " + name);
        }
        return resource.spineData;
    }

    /**
     * @param {PIXI.loaders.Loader} loader
     * @param {*} resource
     * @private
     */
    _onLoaderProgress(loader, resource) {
        this.signals.progress.dispatch(loader, resource, loader.progress);
    }

    /**
     * @param {PIXI.loaders.Loader} loader
     * @param {Array.<*>} resources
     * @private
     */
    _onLoaderComplete(loader, resources) {
        for (let i in resources) {
            if (resources.hasOwnProperty(i)) {
                this._resources[i] = resources[i];
            }
        }
        this._manifest.length = 0;
        this._loader.reset();

        this.signals.complete.dispatch(loader, resources);
    }

    /**
     * @param {string} error
     * @param {PIXI.loaders.Loader} loader
     * @param {Array<*>} resource
     * @private
     */
    _onLoaderError(error, loader, resource) {
        this.signals.error.dispatch(error, loader, resource);
    }

    /**
     * @returns {Object}
     */
    get manifest() {
        return this._manifest.slice();
    }
}
